// src/pages/notifications.js
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMainPageTitle } from "../contexts/PageContext";
import { useNotifications, useMarkAllNotificationsAsRead } from "../hooks/useNotifications";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorMessage from "../components/common/ErrorMessage";
import NotificationHeader from "../components/notifications/NotificationHeader";
import NotificationItem from "../components/notifications/NotificationItem";
import NotificationSettings from "../components/notifications/NotificationSettings";

const EmptyState = ({ t }) => (
  <div className="p-12 text-center">
    <div className="text-gray-400 mb-4">
      <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
          d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" 
        />
      </svg>
    </div>
    <h3 className="text-xl font-semibold text-gray-900 mb-2">{t("notifications.noNotifications")}</h3>
    <p className="text-gray-600">{t("notifications.newNotification")}</p>
  </div>
);

const LoadMoreButton = ({ onLoadMore, isLoading, t }) => (
  <div className="p-6 border-t border-gray-200 text-center">
    <button
      onClick={onLoadMore}
      className="px-8 py-3 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-all duration-200 font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
    >
      {isLoading ? (
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent"></div>
          <span>{t("pages.loading.title")}</span>
        </div>
      ) : (
        t("common.loadMore")
      )}
    </button>
  </div>
);

const Notifications = () => {
  const { t } = useTranslation();
  const { updatePageTitle } = useMainPageTitle();
  
  // React Query hooks
  const { 
    data, 
    isLoading, 
    error, 
    fetchNextPage, 
    hasNextPage, 
    isFetchingNextPage 
  } = useNotifications();
  
  const markAllAsReadMutation = useMarkAllNotificationsAsRead();

  useEffect(() => {
    updatePageTitle(t("notifications.title"), t("notifications.settings"));
  }, [updatePageTitle, t]);
    
  useEffect(() => {
    // Automatically mark all notifications as read when page loads
    console.log("Notifications page loaded - marking all notifications as read");
    markAllAsReadMutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - runs only once on mount

  // Flatten infinite query data
  const notifications = data?.pages?.flatMap(page => page.notifications) || [];

  const loadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const handleMarkAllAsRead = () => {
    markAllAsReadMutation.mutate();
  };

  if (isLoading) {
    return <LoadingSpinner message={t("pages.loading.pleaseWait")} />;
  }

  if (error) {
    return (
      <ErrorMessage 
        message={error.message || t("errors.failedToLoad")} 
        onRetry={() => window.location.reload()}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 space-y-6">
        
        {/* Debug and Settings Panel */}
        <NotificationSettings />
        
        {/* Main Notifications Panel */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <NotificationHeader
            notificationsCount={notifications.length}
            onMarkAllAsRead={handleMarkAllAsRead}
            markAllMutationPending={markAllAsReadMutation.isPending}
          />

          {/* Content */}
          <div className="divide-y divide-gray-200">
            {notifications.length === 0 ? (
              <EmptyState t={t} />
            ) : (
              notifications.map((notification, index) => (
                <NotificationItem
                  key={`${notification._id}-${index}`}
                  notification={notification}
                />
              ))
            )}

            {isFetchingNextPage && (
              <div className="p-6 text-center">
                <div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-blue-500 border-t-transparent"></div>
                <p className="mt-2 text-gray-600 text-sm">{t("pages.loading.pleaseWait")}</p>
              </div>
            )}
          </div>

          {/* Load More Button */}
          {hasNextPage && !isFetchingNextPage && notifications.length > 0 && (
            <LoadMoreButton onLoadMore={loadMore} isLoading={isFetchingNextPage} t={t} />
          )}
        </div>
      </div>
    </div>
  );
};

export default Notifications;
